import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Accordion,
  Modal,
  Form,
  InputGroup,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ProductLanding.css";
import LandingFooter from "../layout/Footer/LandingFooter";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Person,
  Envelope,
  Telephone,
  GeoAlt,
  People,
  ChatDots,
  XLg,
} from "react-bootstrap-icons";
import { HiMiniComputerDesktop } from "react-icons/hi2";
import { GoClock } from "react-icons/go";
import { FaRegBuilding } from "react-icons/fa";
import { showMyAlert } from "../../Utils/alertService";

/**
 * theme
 */
export const COLORS = {
  // Core brand (IDLbay-style warm palette)
  primary: "#FF4B2B",
  primarySoft: "#FF6A4A",
  accent: "#FF9364",
  accentSoft: "#FFE5E0",

  // Backgrounds
  bgLight: "#FFF7F4",
  bgPage: "#FFEFE9",

  // Text
  textDark: "#1F2933",
  textMuted: "#9CA3AF",

  // Utility
  white: "#FFFFFF",
  borderSoft: "#FBD2C7",
};

const USE_CASES = [
  {
    id: "secure-onboarding",
    title: "Secure onboarding & admin control",
    description:
      "Spin up your payroll workspace in minutes with verified admins, role-based access, and centralized control over who can view, edit, and approve payouts.",
  },
  {
    id: "live-visibility",
    title: "Live attendance & payroll visibility",
    description:
      "Monitor presence, overtime, and exceptions in real time, so finance and HR always know what’s accruing before payroll is processed.",
  },
  {
    id: "location-intelligence",
    title: "Location-aware workforce management",
    description:
      "Map branches, define geo-fenced shifts, and ensure employees are tagged to the right site and schedule for accurate payouts and compliance.",
  },
  {
    id: "employee-360",
    title: "360° employee records",
    description:
      "Maintain a single source of truth for employee data—personal details, documents, contracts, histories—kept clean, searchable, and audit-ready.",
  },
  {
    id: "smart-comp",
    title: "Configurable salary & overtime",
    description:
      "Design flexible pay structures, pay periods, and overtime rules that match your policies while the system auto-calculates payouts correctly every time.",
  },
  {
    id: "mapped-workforce",
    title: "Smart assignment & shift coverage",
    description:
      "Quickly assign or bulk-update locations and shifts so staffing, attendance, and payroll stay aligned across teams and regions.",
  },
  {
    id: "leave-automation",
    title: "Automated leave & approvals",
    description:
      "Route leave requests through configurable workflows, view full histories, and approve in clicks—no spreadsheets, no manual chasing.",
  },
  {
    id: "payslip-compliance",
    title: "Compliant, branded payslips",
    description:
      "Generate accurate payslips with your branding, components, and statutory breakdowns, ready for download, email, or audits.",
  },
  {
    id: "governance-analytics",
    title: "Governance & revenue insights",
    description:
      "Control permissions at page-level, track payroll and OT spends, and export reports to Excel/PDF for leadership and statutory reviews.",
  },
];

const ProductLanding = () => {
  const navigate = useNavigate();

  // AOS init
  useEffect(() => {
    AOS.init({
      duration: 600,
      once: true,
      easing: "ease-out-cubic",
      offset: 80,
    });
  }, []);

  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const handleCloseFeedback = () => setShowFeedbackModal(false);

  const handleShowDemo = () => setShowDemoModal(true);
  const handleCloseDemo = () => setShowDemoModal(false);

  const [activeField, setActiveField] = useState("");
  const handleFocus = (field) => setActiveField(field);
  const handleBlur = () => setActiveField("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    employees: "",
    reference: "",
    requirements: "",
  });
  const [feedbackData, setFeedbackData] = useState({
    name: "",
    mobile: "",
    email: "",
    feedback: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getISTDateTime = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(now.getTime() + istOffset);

    const day = String(istTime.getUTCDate()).padStart(2, "0");
    const month = String(istTime.getUTCMonth() + 1).padStart(2, "0");
    const year = istTime.getUTCFullYear();

    const hours = String(istTime.getUTCHours()).padStart(2, "0");
    const minutes = String(istTime.getUTCMinutes()).padStart(2, "0");
    const seconds = String(istTime.getUTCSeconds()).padStart(2, "0");

    return {
      date: `${day}-${month}-${year}`,
      time: `${hours}:${minutes}:${seconds}`,
    };
  };

  const handleSubmitDemo = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const apiUrl =
        "https://api.dremerz.net/api/DZ_8925_PayrollProduct_LandingPage_DemoRequests_DemoRequestsRecords";

      const { date, time } = getISTDateTime();

      const response = await axios.post(apiUrl, {
        ...formData,
        submitionDate: date,
        submitionTime: time,
      });

      if (response.status === 200) {
        showMyAlert({
          type: "Success",
          title: "Data Submitted",
          message: `Your demo request has been submitted successfully!.\n\n You will get a call back from our team.`,
          duration: 2500,
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          country: "",
          employees: "",
          reference: "",
          requirements: "",
        });
        handleCloseDemo();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showMyAlert({
        type: "danger",
        title: "Failed",
        message: `Failed to submit request. Please try again later.`,
        duration: 3500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();

    try {
      const apiUrl =
        "https://api.dremerz.net/api/DZ_8925_PayrollProduct_LandingPage_Feedback_FeedbackRecords";

      await axios.post(apiUrl, {
        name: feedbackData.name,
        mobile: feedbackData.mobile,
        email: feedbackData.email,
        feedback: feedbackData.feedback,
        submitionDate: getISTDateTime().date,
        submitionTime: getISTDateTime().time,
      });

      showMyAlert({
        type: "Success",
        title: "Thank you!",
        message: "Your feedback has been submitted successfully.",
        duration: 3000,
      });

      setFeedbackData({
        name: "",
        mobile: "",
        email: "",
        feedback: "",
      });
      handleCloseFeedback();
    } catch (err) {
      showMyAlert({
        type: "danger",
        title: "Failed",
        message: "Unable to submit feedback. Try again later.",
        duration: 3000,
      });
    }
  };

  return (
    <div
      className="productLanding-root"
      style={{ backgroundColor: COLORS.bgPage, color: COLORS.textDark }}
    >
      {/* ============= HERO ============= */}

      {/* ============= HEADER NAV ============= */}
      <header
        className="productLanding-header"
        style={{ backgroundColor: COLORS.bgLight }}
      >
        <Container fluid className="px-3 px-lg-5">
          <div className="d-flex justify-content-center justify-content-md-between align-items-center py-3">
            {/* Logo or Brand Name */}
            <div className="me-3">
              <Button
                variant="link"
                className="productLanding-header-link"
                onClick={() => navigate("/ProductPricing")}
              >
                Pricing
              </Button>
            </div>

            <div className="d-flex align-items-center gap-3">
              <Button
                variant="link"
                className="productLanding-header-link"
                onClick={() => setShowFeedbackModal(true)}
              >
                Feedback
              </Button>

              <Button
                variant="link"
                className="productLanding-header-link"
                href="https://idlbay.com/contactus"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </Container>
      </header>

      <section
        className="productLanding-hero"
        style={{ backgroundColor: COLORS.bgLight }}
      >
        <Container fluid className="px-3 px-lg-5 h-100 py-4 py-md-0">
          <Row className="h-100 justify-content-center align-items-center">
            <Col md={10} lg={7} data-aos="fade-up">
              <div className="productLanding-hero-label">
                Smart payroll &amp; reserves
              </div>

              <h1 className="productLanding-hero-title">
                Get paid early.
                <br />
                Protect every payout.
                <br />
                Control it from one place.
              </h1>

              <p className="productLanding-hero-subtitle">
                A modern, company-first payout platform to run salaries with
                full visibility, policy control, and instant access when you
                need it.
              </p>

              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Button
                  onClick={() => navigate("/RegisterPage")}
                  className="productLanding-hero-btn-primary"
                  style={{ backgroundColor: COLORS.primary, border: "none" }}
                >
                  Get started in minutes
                </Button>
                <Button
                  onClick={handleShowDemo}
                  variant="outline-secondary"
                  className="productLanding-hero-btn-outline"
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.borderSoft,
                    backgroundColor: COLORS.white,
                  }}
                >
                  Book a demo
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============= WHY / FEATURES ============= */}
      <section className="productLanding-why-section py-5 py-md-6">
        <Container fluid className="px-3 px-lg-5">
          <Row className="justify-content-center text-center mb-4">
            <Col lg={8} data-aos="fade-up">
              <div className="productLanding-section-label">
                Why choose our product
              </div>
              <h2 className="productLanding-section-title">
                Built for finance teams, not side tools.
              </h2>
              <p className="productLanding-section-subtitle">
                Simple, transparent, and designed so your team can move large
                volumes of payouts with confidence and control.
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center g-4 mb-2" data-aos="fade-up">
            <Col xs={12} sm={6} lg={4}>
              <Card className="productLanding-feature-card">
                <h6>Unlimited security</h6>
                <p>
                  Enterprise-grade protection for company balances and payroll
                  flows end-to-end.
                </p>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Card className="productLanding-feature-card">
                <h6>Multiple accounts</h6>
                <p>
                  Run operating, payroll, and reserve accounts from one control
                  center with granular permissions.
                </p>
              </Card>
            </Col>
          </Row>

          <Row className="justify-content-center g-4 mb-5" data-aos="fade-up">
            <Col xs={12} sm={6} lg={4}>
              <Card className="productLanding-feature-card">
                <h6>Free transfers</h6>
                <p>
                  Move funds between internal accounts and payout rails without
                  additional platform fees.
                </p>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Card className="productLanding-feature-card">
                <h6>Scale ready</h6>
                <p>
                  Built for high-volume payouts, multiple entities, and complex
                  approval workflows.
                </p>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============= USE CASES ============= */}
      <section className="productLanding-usecases py-5">
        <Container className="px-3 px-lg-5">
          <Row className="justify-content-center text-center mb-4">
            <Col lg={8} data-aos="fade-up">
              <div className="productLanding-section-label">Use cases</div>
              <h2 className="productLanding-section-title">
                Designed for real-world payroll operations.
              </h2>
              <p className="productLanding-section-subtitle">
                IDLBAY Payroll powers everything from onboarding to audits, so
                finance, HR, and operations can run payroll as a controlled,
                visible process— not a last-minute fire drill.
              </p>
            </Col>
          </Row>

          <Row className="g-4" data-aos="fade-up">
            {USE_CASES.map((item) => (
              <Col xs={12} md={6} lg={4} key={item.id}>
                <Card className="productLanding-usecase-card">
                  <div className="productLanding-usecase-title">
                    {item.title}
                  </div>
                  <p className="productLanding-usecase-text">
                    {item.description}
                  </p>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ============= FAQ ============= */}
      <section className="productLanding-faq-section py-5">
        <Container className="px-3 px-lg-5">
          <Row className="justify-content-center text-center mb-4">
            <Col lg={7} data-aos="fade-up">
              <div className="productLanding-section-label">FAQ</div>
              <h2 className="productLanding-section-title">
                Answers before you even ask finance.
              </h2>
              <p className="productLanding-section-subtitle">
                Clear terms, transparent pricing, and no surprises. Here’s what
                most teams want to know first.
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={8} data-aos="fade-up">
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    How quickly can we launch with Product?
                  </Accordion.Header>
                  <Accordion.Body>
                    Most teams go live in under a week. Connect your bank
                    accounts, sync payroll data, define approvals, and run a
                    test cycle before switching production payouts.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    How do you keep our funds and data secure?
                  </Accordion.Header>
                  <Accordion.Body>
                    We work with regulated banking partners, segregate client
                    funds, encrypt data in transit and at rest, and enforce
                    role-based access with full audit trails.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    Can we integrate our existing payroll, ERP, or accounting
                    tools?
                  </Accordion.Header>
                  <Accordion.Body>
                    Yes. Use direct integrations or secure file/API sync to
                    connect payroll systems, ERPs, and accounting platforms
                    without disrupting your current stack.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    How is pricing structured?
                  </Accordion.Header>
                  <Accordion.Body>
                    Transparent, usage-based pricing aligned to payout volume
                    and features. No setup fees and no hidden markups on
                    transactions.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    Do you support complex approvals and audit trails?
                  </Accordion.Header>
                  <Accordion.Body>
                    Absolutely. Configure multi-level approvals by amount,
                    entity, or payout type and export audit-ready logs anytime
                    for internal or external reviews.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="5">
                  <Accordion.Header>
                    Is there a minimum volume or long-term lock-in?
                  </Accordion.Header>
                  <Accordion.Body>
                    No lock-in. Start with a pilot, scale as needed, and export
                    your data and history in clean formats if you ever decide to
                    move.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============= CTA ============= */}
      <section className="productLanding-cta-section py-4 py-md-5">
        <Container className="px-3 px-lg-5">
          <div className="productLanding-cta-wrapper" data-aos="fade-up">
            <Row className="align-items-center g-4">
              <Col className="d-flex flex-column justify-content-center align-items-center">
                <h3 className="productLanding-cta-title">
                  Ready to modernize your payouts?
                  <br />
                  Start your 7-day free trial.
                </h3>
                <p className="productLanding-cta-subtitle">
                  Launch a guided trial and see how unified payroll &amp;
                  reserves work in your real environment.
                </p>
                <Button
                  onClick={() => navigate("/RegisterPage")}
                  className="productLanding-cta-btn"
                  style={{
                    backgroundColor: COLORS.white,
                    color: COLORS.primary,
                  }}
                >
                  Start now
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* ============= DEMO MODAL (Book a demo) ============= */}
      <Modal
        show={showDemoModal}
        onHide={handleCloseDemo}
        size="lg"
        centered
        className="productLanding-demo-modal p-1"
      >
        <Modal.Body className="p-0">
          <Row className="g-0">
            {/* Left info pane */}
            <Col
              md={5}
              className="productLanding-left-pane p-4 d-flex flex-column justify-content-between position-relative"
            >
              {/* Close (mobile) */}
              <Button
                variant="light"
                className="position-absolute top-0 end-0 m-2 rounded-circle d-flex align-items-center justify-content-center d-md-none"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "none",
                  background: "rgba(255, 255, 255, 0.9)",
                  zIndex: 10,
                }}
                onClick={handleCloseDemo}
              >
                <XLg size={20} />
              </Button>

              <div>
                <h4 className="fw-bold mt-4 mb-3">
                  Get expert help to{" "}
                  <span style={{ color: COLORS.primary }}>
                    transform payroll
                  </span>{" "}
                  for your company
                </h4>
              </div>

              <ul className="list-unstyled fw-semibold mt-2 productLanding-feature-list">
                <li>
                  <span className="productLanding-icon-wrapper bg-white text-danger">
                    <HiMiniComputerDesktop />
                  </span>
                  1:1 onboarding support for every pilot.
                </li>
                <li>
                  <span className="productLanding-icon-wrapper bg-white text-warning">
                    <GoClock />
                  </span>
                  Cut reconciliation &amp; approval time drastically.
                </li>
                <li>
                  <span className="productLanding-icon-wrapper bg-white text-danger">
                    <FaRegBuilding />
                  </span>
                  Built for multi-entity, multi-location organizations.
                </li>
              </ul>

              <blockquote className="fst-italic mt-3 small">
                “The switch was seamless. Control, visibility, and support have
                been exceptional.”
              </blockquote>
            </Col>

            {/* Right form pane */}
            <Col md={7} className="p-4 bg-light position-relative">
              {/* Close (desktop) */}
              <Button
                variant="light"
                className="position-absolute top-0 end-0 m-2 rounded-circle d-none d-md-flex align-items-center justify-content-center"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "none",
                  background: "rgba(255, 255, 255, 0.9)",
                  zIndex: 10,
                }}
                onClick={handleCloseDemo}
              >
                <XLg size={20} />
              </Button>

              <h4 className="fw-bold mb-3 text-center">Book a live demo</h4>

              <Form onSubmit={handleSubmitDemo}>
                {/* Name */}
                <Form.Group className="mb-3">
                  <InputGroup
                    className={`productLanding-input-group-custom ${
                      activeField === "name" ? "active" : ""
                    }`}
                  >
                    <InputGroup.Text>
                      <Person />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Your full name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => handleFocus("name")}
                      onBlur={handleBlur}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                {/* Email */}
                <Form.Group className="mb-3">
                  <InputGroup
                    className={`productLanding-input-group-custom ${
                      activeField === "email" ? "active" : ""
                    }`}
                  >
                    <InputGroup.Text>
                      <Envelope />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      placeholder="Work email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => handleFocus("email")}
                      onBlur={handleBlur}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                {/* Phone */}
                <Form.Group className="mb-3">
                  <InputGroup
                    className={`productLanding-input-group-custom ${
                      activeField === "phone" ? "active" : ""
                    }`}
                  >
                    <InputGroup.Text>
                      <Telephone />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Phone number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => handleFocus("phone")}
                      onBlur={handleBlur}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                {/* Country */}
                <Form.Group className="mb-3">
                  <InputGroup
                    className={`productLanding-input-group-custom ${
                      activeField === "country" ? "active" : ""
                    }`}
                  >
                    <InputGroup.Text>
                      <GeoAlt />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      onFocus={() => handleFocus("country")}
                      onBlur={handleBlur}
                    />
                  </InputGroup>
                </Form.Group>

                {/* Employees */}
                <Form.Group className="mb-3">
                  <InputGroup
                    className={`productLanding-input-group-custom ${
                      activeField === "employees" ? "active" : ""
                    }`}
                  >
                    <InputGroup.Text>
                      <People />
                    </InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="Number of employees"
                      name="employees"
                      value={formData.employees}
                      onChange={handleChange}
                      onFocus={() => handleFocus("employees")}
                      onBlur={handleBlur}
                    />
                  </InputGroup>
                </Form.Group>

                {/* Reference */}
                <Form.Group className="mb-3">
                  <InputGroup
                    className={`productLanding-input-group-custom ${
                      activeField === "reference" ? "active" : ""
                    }`}
                  >
                    <InputGroup.Text>
                      <ChatDots />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="How did you hear about us?"
                      name="reference"
                      value={formData.reference}
                      onChange={handleChange}
                      onFocus={() => handleFocus("reference")}
                      onBlur={handleBlur}
                    />
                  </InputGroup>
                </Form.Group>

                {/* Requirements */}
                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Tell us about your payroll requirements"
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Text className="text-muted small d-block mb-3 text-center">
                  By submitting, you agree to our Privacy Policy.
                </Form.Text>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-100 rounded-pill border-0"
                  style={{ background: COLORS.primary, color: "#fff" }}
                >
                  {loading ? "Submitting..." : "Submit my request"}
                </Button>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      {/* ============= FEEDBACK MODAL ============= */}

      <Modal
        show={showFeedbackModal}
        onHide={handleCloseFeedback}
        size="lg"
        centered
        className="productLanding-feedback-modal p-1"
      >
        <Modal.Body className="p-0">
          <Row className="g-0">
            {/* Left info pane */}
            <Col
              md={5}
              className="productLanding-left-pane p-4 d-flex flex-column justify-content-between position-relative"
            >
              {/* Close (mobile) */}
              <Button
                variant="light"
                className="position-absolute top-0 end-0 m-2 rounded-circle d-flex align-items-center justify-content-center d-md-none"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "none",
                  background: "rgba(255, 255, 255, 0.9)",
                  zIndex: 10,
                }}
                onClick={handleCloseFeedback}
              >
                <XLg size={20} />
              </Button>

              <div>
                <h4 className="fw-bold mt-4 mb-3">
                  We value your{" "}
                  <span style={{ color: COLORS.primary }}>feedback</span>
                </h4>
                <p className="small text-secondary mb-4">
                  Share your thoughts and help us improve your experience with
                  <strong> IDLBAY Payroll</strong>.
                </p>
              </div>

              <ul className="list-unstyled fw-semibold mt-2 productLanding-feature-list">
                <li>
                  <span className="productLanding-icon-wrapper bg-white text-danger">
                    <ChatDots />
                  </span>
                  Your ideas matter — every suggestion counts.
                </li>
                <li>
                  <span className="productLanding-icon-wrapper bg-white text-warning">
                    <GoClock />
                  </span>
                  We typically respond within 24 hours.
                </li>
              </ul>

              <blockquote className="fst-italic mt-3 small">
                “Listening to our users is how we evolve.”
              </blockquote>
            </Col>

            {/* Right form pane */}
            <Col md={7} className="p-5 bg-light position-relative">
              {/* Close (desktop) */}
              <Button
                variant="light"
                className="position-absolute top-0 end-0 m-2 rounded-circle d-none d-md-flex align-items-center justify-content-center"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "none",
                  background: "rgba(255, 255, 255, 0.9)",
                  zIndex: 10,
                }}
                onClick={handleCloseFeedback}
              >
                <XLg size={20} />
              </Button>

              <h4 className="fw-bold mb-4 text-center">Share your feedback</h4>

              <Form onSubmit={handleSubmitFeedback}>
                <Form.Group className="mb-3">
                  <InputGroup className="productLanding-input-group-custom">
                    <InputGroup.Text>
                      <Person />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Your name"
                      value={feedbackData.name}
                      onChange={(e) =>
                        setFeedbackData({
                          ...feedbackData,
                          name: e.target.value,
                        })
                      }
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <InputGroup className="productLanding-input-group-custom">
                    <InputGroup.Text>
                      <Telephone />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Mobile number"
                      value={feedbackData.mobile}
                      onChange={(e) =>
                        setFeedbackData({
                          ...feedbackData,
                          mobile: e.target.value,
                        })
                      }
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <InputGroup className="productLanding-input-group-custom">
                    <InputGroup.Text>
                      <Envelope />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      placeholder="Email (optional)"
                      value={feedbackData.email}
                      onChange={(e) =>
                        setFeedbackData({
                          ...feedbackData,
                          email: e.target.value,
                        })
                      }
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Write your feedback..."
                    value={feedbackData.feedback}
                    onChange={(e) =>
                      setFeedbackData({
                        ...feedbackData,
                        feedback: e.target.value,
                      })
                    }
                    required
                  />
                </Form.Group>

                <Button
                  type="submit"
                  className="w-100 rounded-pill border-0 py-2"
                  style={{ background: COLORS.primary, color: "#fff" }}
                >
                  Submit Feedback
                </Button>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <LandingFooter />
    </div>
  );
};

export default ProductLanding;
